import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import FilterAltOutlined from '@mui/icons-material/FilterAltOutlined';
import { ProgramTypeEnum } from '../../../Enum/ProgramTypeEnum';
import {
    IOnboardingDashboardDetail,
    OnboardingDashboardCardType
} from '../../../Models/Widget/OnboardingPerformanceDashboardDetail.model';
import {
    exportOnboardingDashboardDetails,
    fetchOnboardingDashboardDetails
} from '../../Widgets/OnboardingWidgetService';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { getOnboarderProviders } from '../../AdminOnboarding/OnboarderGoals/OnboardingGoalService';
import { IOnboarderProvider } from '../../../Models/AdminOnboarding/OnboarderGoal.model';
import { parentUrl } from '../../../utils/constants';
import b64toBlob from 'b64-to-blob';
import { DatePicker, DateView, DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { OnboardingWidgetDateRangeEnum } from '../../../Enum/OnboardingWidgetDateRangeEnum';

interface DetailColumn {
    key: keyof IOnboardingDashboardDetail;
    label: string;
    align?: 'left' | 'center' | 'right';
    format?: (row: IOnboardingDashboardDetail) => React.ReactNode;
}

const programOptions = [
    { value: ProgramTypeEnum.All, label: 'All' },
    { value: ProgramTypeEnum.FullProgram, label: 'Full Program' },
    { value: ProgramTypeEnum.HRA, label: 'HRA' },
    { value: ProgramTypeEnum.FUHFUM, label: 'FUHFUM' },
    { value: ProgramTypeEnum.andNext, label: 'andNext' }
];

const formatDate = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

const OnboardingDashboardDetail: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const {
        onboardingProviderId,
        setOnboardingProviderId,
        onboardingProgramType,
        setOnboardingProgramType,
        onboardingCardType,
        onboardingSelectedYear,
        setOnboardingSelectedYear,
        onboardingSelectedMonth,
        setOnboardingSelectedMonth
    } = useWidgetContext();

    const [details, setDetails] = useState<IOnboardingDashboardDetail[]>([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [providers, setProviders] = useState<IOnboarderProvider[]>([]);
    const [pickerDate, setPickerDate] = useState(new Date(onboardingSelectedYear, onboardingSelectedMonth - 1, 1));
    const [pickerView, setPickerView] = useState<DateView>('month');
    const hasElevatedFilterAccess = searchParams.get('hasElevatedFilterAccess') === 'true';
    const [selectedFilter, setSelectedFilter] = useState<OnboardingWidgetDateRangeEnum>(OnboardingWidgetDateRangeEnum.Last3Days);
    const [activityFilter, setActivityFilter] = useState<OnboardingWidgetDateRangeEnum>(OnboardingWidgetDateRangeEnum.Last1Day);
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const [activityFromDate, setActivityFromDate] = useState<Date | null>(null);
    const [activityToDate, setActivityToDate] = useState<Date | null>(null);

    const dayFilterList = [
        { Id: OnboardingWidgetDateRangeEnum.Last1Day, Value: OnboardingWidgetDateRangeEnum.Last1DayDescription },
        { Id: OnboardingWidgetDateRangeEnum.Last3Days, Value: OnboardingWidgetDateRangeEnum.Last3DaysDescription },
        { Id: OnboardingWidgetDateRangeEnum.Last7Days, Value: OnboardingWidgetDateRangeEnum.Last7DaysDescription },
        { Id: OnboardingWidgetDateRangeEnum.Custom, Value: OnboardingWidgetDateRangeEnum.CustomDescription },
    ];

    const activityFilterList = [
        { Id: OnboardingWidgetDateRangeEnum.Last1Day, Value: 'Today' },
        { Id: OnboardingWidgetDateRangeEnum.ThisWeek, Value: 'This Week' },
        { Id: OnboardingWidgetDateRangeEnum.LastWeek, Value: 'Last Week' },
        { Id: OnboardingWidgetDateRangeEnum.ThisMonth, Value: 'This Month' },
        { Id: OnboardingWidgetDateRangeEnum.LastMonth, Value: 'Last Month' },
        { Id: OnboardingWidgetDateRangeEnum.Custom, Value: 'Custom' }
    ];

    const isActivityCard = onboardingCardType === OnboardingDashboardCardType.ScheduledToday || onboardingCardType === OnboardingDashboardCardType.CallsMadeToday;

    const getProviderName = (
        provider?: IOnboarderProvider
    ): string => {
        if (!provider) {
            return '';
        }

        return `${provider.FirstName ?? ''} ${provider.LastName ?? ''
            }`.trim();
    };

    const loadProviders = useCallback(async () => {
        try {
            const providerList = await getOnboarderProviders(
                'Office Onboarding',
                false
            );

            const sortedProviders = [...providerList].sort((first, second) =>
                getProviderName(first).localeCompare(getProviderName(second))
            );

            setProviders([
                ...sortedProviders,
                {
                    Id: 'others',
                    FirstName: 'Others',
                    LastName: ''
                }
            ]);
        } catch (error) {
            console.error(error);
        }
    }, []);

    useEffect(() => {
        void loadProviders();
    }, [loadProviders]);

    const loadDetails = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            let result: IOnboardingDashboardDetail[];

            if (onboardingCardType === OnboardingDashboardCardType.UndocumentedOnboards) {
                if (selectedFilter !== OnboardingWidgetDateRangeEnum.Custom || (Boolean(fromDate) && Boolean(toDate))) {
                    const dateFrom = fromDate && selectedFilter === OnboardingWidgetDateRangeEnum.Custom ? new Date(fromDate).toISOString() : '';
                    const dateTo = toDate && selectedFilter === OnboardingWidgetDateRangeEnum.Custom ? new Date(toDate).toISOString() : '';

                    result = await fetchOnboardingDashboardDetails(
                        onboardingCardType,
                        onboardingProviderId,
                        onboardingProgramType,
                        onboardingSelectedYear,
                        onboardingSelectedMonth,
                        selectedFilter,
                        dateFrom,
                        dateTo
                    );

                    setDetails(result);
                    setPage(0);
                }
            } else if (isActivityCard) {
                if (activityFilter !== OnboardingWidgetDateRangeEnum.Custom || (Boolean(activityFromDate) && Boolean(activityToDate))) {
                    const dateFrom = activityFromDate && activityFilter === OnboardingWidgetDateRangeEnum.Custom ? new Date(activityFromDate).toISOString() : '';
                    const dateTo = activityToDate && activityFilter === OnboardingWidgetDateRangeEnum.Custom ? new Date(activityToDate).toISOString() : '';

                    result = await fetchOnboardingDashboardDetails(
                        onboardingCardType,
                        onboardingProviderId,
                        onboardingProgramType,
                        onboardingSelectedYear,
                        onboardingSelectedMonth,
                        activityFilter,
                        dateFrom,
                        dateTo
                    );

                    setDetails(result);
                    setPage(0);
                }
            } else {
                result = await fetchOnboardingDashboardDetails(
                    onboardingCardType,
                    onboardingProviderId,
                    onboardingProgramType,
                    onboardingSelectedYear,
                    onboardingSelectedMonth
                );

                setDetails(result);
                setPage(0);
            }
        } catch (err) {
            console.error(err);
            setDetails([]);
            setError('Unable to load onboarding dashboard details.');
        } finally {
            setLoading(false);
        }
    }, [
        onboardingCardType,
        onboardingProviderId,
        onboardingProgramType,
        onboardingSelectedYear,
        onboardingSelectedMonth,
        selectedFilter,
        fromDate,
        toDate,
        activityFilter,
        activityFromDate,
        activityToDate,
        isActivityCard
    ]);

    useEffect(() => {
        loadDetails();
    }, [loadDetails]);

    const columns = useMemo<DetailColumn[]>(() => {
        switch (onboardingCardType) {
            case OnboardingDashboardCardType.MonthlyGoal:
            case OnboardingDashboardCardType.NeededToday:
                return [
                    { key: 'ProviderName', label: 'Provider' },
                    { key: 'MonthName', label: 'Month', align: 'center' },
                    { key: 'Year', label: 'Year', align: 'center' },
                    { key: 'ProgramName', label: 'Program', align: 'center' },
                    { key: 'GoalCount', label: 'Goal', align: 'right' }
                ];

            case OnboardingDashboardCardType.NeededForRestOfMonth:
                return [
                    { key: 'ProviderName', label: 'Provider' },
                    { key: 'MonthName', label: 'Month', align: 'center' },
                    { key: 'Year', label: 'Year', align: 'center' },
                    { key: 'ProgramName', label: 'Program', align: 'center' },
                    { key: 'MonthlyGoal', label: 'Monthly Goal', align: 'right' },
                    { key: 'CompletedCount', label: 'Completed MTD', align: 'right' },
                    { key: 'RemainingGoal', label: 'Remaining Goal', align: 'right' },
                    { key: 'ConversionRateMonthToDate', label: 'Conversion Rate', align: 'right', format: row => row.ConversionRateMonthToDate == null ? 'N/A' : row.ConversionRateMonthToDate + '%' },
                    { key: 'SchedulingConversionRate', label: 'Scheduling Conversion Rate', align: 'right', format: row => row.SchedulingConversionRate == null ? 'N/A' : row.SchedulingConversionRate + '%' },
                    { key: 'SchedulingConversionMonth', label: 'Conversion Source Month', format: row => row.SchedulingConversionYear != null && row.SchedulingConversionMonth != null ? new Date(row.SchedulingConversionYear, row.SchedulingConversionMonth - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A' },
                    { key: 'AppointmentsRequired', label: 'Appointments Required', align: 'right', format: row => row.AppointmentsRequired ?? 'N/A' },
                    { key: 'UpcomingScheduledMembers', label: 'Upcoming Scheduled Members', align: 'right' },
                    { key: 'NeededForRestOfMonth', label: 'To Schedule', align: 'right', format: row => row.NeededForRestOfMonth ?? 'N/A' }
                ];

            case OnboardingDashboardCardType.CompletedMonthToDate:
                return [
                    { key: 'AhcccsId', label: 'AHCCCS ID' },
                    { key: 'MemberName', label: 'Member' },
                    { key: 'ProviderName', label: 'Onboarder' },
                    { key: 'CompletedDate', label: 'Completed Date', format: row => formatDate(row.CompletedDate) },
                ];

            case OnboardingDashboardCardType.ScheduledToday:
                return [
                    { key: 'AhcccsId', label: 'AHCCCS ID' },
                    { key: 'MemberName', label: 'Member' },
                    { key: 'ProviderName', label: 'Onboarder' },
                    { key: 'AppointmentDate', label: 'Appointment Date', format: row => formatDate(row.AppointmentDate) },
                    { key: 'AppointmentTime', label: 'Time' },
                    { key: 'Status', label: 'Status' }
                ];

            case OnboardingDashboardCardType.ConversionRateMonthToDate:
                return [
                    { key: 'AhcccsId', label: 'AHCCCS ID' },
                    { key: 'MemberName', label: 'Member' },
                    { key: 'ProviderName', label: 'Onboarder' },
                    { key: 'AppointmentDate', label: 'Scheduled Date', format: row => formatDate(row.AppointmentDate) },
                    { key: 'CompletedDate', label: 'Completed Date', format: row => formatDate(row.CompletedDate) },
                    { key: 'Status', label: 'Conversion Status' }
                ];

            case OnboardingDashboardCardType.UpcomingAppointmentsThisMonth:
            case OnboardingDashboardCardType.UpcomingAppointmentsNextMonth:
                return [
                    { key: 'AhcccsId', label: 'AHCCCS ID' },
                    { key: 'MemberName', label: 'Member' },
                    { key: 'ProviderName', label: 'Onboarder' },
                    { key: 'AppointmentDate', label: 'Appointment Date', format: row => formatDate(row.AppointmentDate) },
                    { key: 'AppointmentTime', label: 'Time' },
                    { key: 'Status', label: 'Status' },
                ];

            case OnboardingDashboardCardType.RescheduledThisMonth:
                return [
                    { key: 'AhcccsId', label: 'AHCCCS ID' },
                    { key: 'MemberName', label: 'Member' },
                    { key: 'ProviderName', label: 'Onboarder' },
                    { key: 'PreviousAppointmentDate', label: 'Previous Appointment', format: row => formatDate(row.PreviousAppointmentDate) },
                    { key: 'RescheduledDate', label: 'Rescheduled Appointment', format: row => formatDate(row.RescheduledDate) },
                    { key: 'Status', label: 'Status' }
                ];

            case OnboardingDashboardCardType.NeedsReschedule:
                return [
                    { key: 'AhcccsId', label: 'AHCCCS ID' },
                    { key: 'MemberName', label: 'Member' },
                    { key: 'ProviderName', label: 'Onboarder' },
                    { key: 'AppointmentDate', label: 'Previous Appointment', format: row => formatDate(row.AppointmentDate) },
                    { key: 'Status', label: 'Status' },
                ];

            case OnboardingDashboardCardType.CallsMadeToday:
                return [
                    { key: 'MemberName', label: 'Member Name' },
                    { key: 'ProviderName', label: 'Onboarder' },
                    {
                        key: 'CallDate',
                        label: 'Call Date',
                        format: (row: IOnboardingDashboardDetail) =>
                            row.CallDate ? new Date(row.CallDate).toLocaleDateString() : '-',
                    },
                    { key: 'CallTime', label: 'Call Time' },
                    { key: 'PopulationName', label: 'Population' },
                    { key: 'PopulationNumber', label: 'Population #' },
                    { key: 'PhoneString', label: 'Phone' },
                ];

            case OnboardingDashboardCardType.UndocumentedOnboards:
                return [
                    { key: 'ProviderName', label: 'Provider' },
                    { key: 'MemberName', label: 'Member Name' },
                    {
                        key: 'AppointmentDate',
                        label: 'Appointment Date',
                        format: (row: IOnboardingDashboardDetail) =>
                            row.AppointmentDate
                                ? new Date(row.AppointmentDate).toLocaleDateString()
                                : '-',
                    },
                    { key: 'AppointmentTime', label: 'Appointment Time' },
                ];

            default:
                return [];
        }
    }, [onboardingCardType]);

    const handleExport = async () => {
        try {
            setExporting(true);

            let timeFilter: OnboardingWidgetDateRangeEnum | undefined;
            let exportDateFrom: string | undefined;
            let exportDateTo: string | undefined;

            if (onboardingCardType === OnboardingDashboardCardType.UndocumentedOnboards) {
                timeFilter = selectedFilter;
                exportDateFrom = selectedFilter === OnboardingWidgetDateRangeEnum.Custom && fromDate ? fromDate.toISOString() : undefined;
                exportDateTo = selectedFilter === OnboardingWidgetDateRangeEnum.Custom && toDate ? toDate.toISOString() : undefined;
            } else if (isActivityCard) {
                timeFilter = activityFilter;
                exportDateFrom = activityFilter === OnboardingWidgetDateRangeEnum.Custom && activityFromDate ? activityFromDate.toISOString() : undefined;
                exportDateTo = activityFilter === OnboardingWidgetDateRangeEnum.Custom && activityToDate ? activityToDate.toISOString() : undefined;
            }

            const data = await exportOnboardingDashboardDetails(
                onboardingCardType,
                onboardingProviderId,
                onboardingProgramType,
                onboardingSelectedYear,
                onboardingSelectedMonth,
                timeFilter,
                exportDateFrom,
                exportDateTo
            );

            const blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;

            const date = new Date();
            const mm = date.getMonth() + 1;
            const dd = date.getDate();
            const dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');

            link.setAttribute('download', `${title} - ${dateInfo}.xlsx`);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Unable to export onboarding dashboard details.', error);
        } finally {
            setExporting(false);
        }
    };

    const pagedDetails = useMemo(() => {
        const start = page * rowsPerPage;
        return details.slice(start, start + rowsPerPage);
    }, [details, page, rowsPerPage]);

    const currentDate = new Date();

    const isCurrentMonth =
        onboardingSelectedYear === currentDate.getFullYear() &&
        onboardingSelectedMonth === currentDate.getMonth() + 1;

    const selectedMonthDate = new Date(onboardingSelectedYear, onboardingSelectedMonth - 1, 1);

    const selectedMonthName = selectedMonthDate.toLocaleString('default', {
        month: 'long',
        year: 'numeric'
    });

    const nextMonthDate = new Date(onboardingSelectedYear, onboardingSelectedMonth, 1);

    const nextMonthName = nextMonthDate.toLocaleString('default', {
        month: 'long',
        year: 'numeric'
    });

    const selectedMonthSuffix = isCurrentMonth ? '' : ` (${selectedMonthName})`;
    const nextMonthSuffix = isCurrentMonth ? '' : ` (${nextMonthName})`;

    const cardTitles: Record<OnboardingDashboardCardType, string> = {
        [OnboardingDashboardCardType.MonthlyGoal]: 'Monthly Goal',
        [OnboardingDashboardCardType.CompletedMonthToDate]: `Completed MTD${selectedMonthSuffix}`,
        [OnboardingDashboardCardType.NeededToday]: 'Needed Today',
        [OnboardingDashboardCardType.NeededForRestOfMonth]: 'To Schedule',
        [OnboardingDashboardCardType.ScheduledToday]: 'Scheduled Onboards',
        [OnboardingDashboardCardType.ConversionRateMonthToDate]: `Conversion Rate MTD${selectedMonthSuffix}`,
        [OnboardingDashboardCardType.UpcomingAppointmentsThisMonth]: `Upcoming Appointments - This Month${selectedMonthSuffix}`,
        [OnboardingDashboardCardType.UpcomingAppointmentsNextMonth]: `Upcoming Appointments - Next Month${nextMonthSuffix}`,
        [OnboardingDashboardCardType.RescheduledThisMonth]: `Rescheduled This Month${selectedMonthSuffix}`,
        [OnboardingDashboardCardType.NeedsReschedule]: 'Needs Reschedule',
        [OnboardingDashboardCardType.CallsMadeToday]: 'Calls Made',
        [OnboardingDashboardCardType.UndocumentedOnboards]: 'Undocumented Onboards'
    };

    const title = cardTitles[onboardingCardType] || 'Onboarding Details';

    const navigateToMemberDashboard = (id: string) => {
        window.open(`${parentUrl}provider/members-list/member-chart/${id}/member-profile`, '_blank');
    };

    return (
        <Paper elevation={5}>
            <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f7f8fa', minHeight: '100vh', width: '100%' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Tooltip title="Back to dashboard">
                                <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                                    <ArrowBackOutlined />
                                </IconButton>
                            </Tooltip>

                            <Box>
                                <Typography variant="h5" fontWeight={700}>Onboarding Details</Typography>
                                <Typography variant="body2" color="text.secondary">View and export detailed onboarding performance information.</Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlined />} onClick={handleExport} disabled={exporting || loading || details.length === 0}>
                                {exporting ? 'Exporting...' : 'Export'}
                            </Button>
                        </Stack>
                    </Stack>

                    {onboardingCardType === OnboardingDashboardCardType.NeededForRestOfMonth && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Estimates use each provider's MTD conversion, falling back to the previous calendar month's rate when zero or unavailable, and distinct upcoming members. To Schedule is N/A only when neither month has a positive rate and a goal remains.
                            Provider estimates may not add up to the dashboard estimate, which uses the selected group's conversion and distinct members.
                        </Typography>
                    )}
                    <Card elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <CardContent>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                <FilterAltOutlined color="action" />
                                <Typography fontWeight={600}>Filters</Typography>
                            </Stack>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 2
                                }}
                            >
                                {onboardingCardType !== OnboardingDashboardCardType.UndocumentedOnboards && !isActivityCard && (
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label="Month"
                                            views={['year', 'month']}
                                            openTo="month"
                                            value={pickerDate}
                                            onViewChange={view => setPickerView(view)}
                                            onChange={value => {
                                                if (!value) return;

                                                setPickerDate(value);

                                                if (pickerView !== 'month') return;

                                                setOnboardingSelectedYear(value.getFullYear());
                                                setOnboardingSelectedMonth(value.getMonth() + 1);
                                            }}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    sx: { width: 170 }
                                                },
                                                actionBar: {
                                                    actions: []
                                                },
                                                popper: {
                                                    sx: {
                                                        '& .MuiDateCalendar-root': {
                                                            height: 'auto',
                                                            minHeight: 0
                                                        },
                                                        '& .MuiPickersSlideTransition-root': {
                                                            minHeight: 'auto'
                                                        },
                                                        '& .MuiMonthCalendar-root': {
                                                            height: 'auto',
                                                            minHeight: 0,
                                                            pb: 1
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                )}

                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel>Program</InputLabel>
                                    <Select<ProgramTypeEnum>
                                        value={onboardingProgramType}
                                        label="Program"
                                        onChange={event => setOnboardingProgramType(event.target.value as ProgramTypeEnum)}
                                    >
                                        {programOptions.map(option => (
                                            <MenuItem key={String(option.value)} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" disabled={!hasElevatedFilterAccess} sx={{ minWidth: 180 }}>
                                    <InputLabel>Onboarder</InputLabel>
                                    <Select
                                        value={onboardingProviderId}
                                        label="Onboarder"
                                        onChange={event => setOnboardingProviderId(event.target.value)}
                                    >
                                        <MenuItem value="All">All</MenuItem>

                                        {providers.map(provider => (
                                            <MenuItem key={provider.Id} value={provider.Id}>
                                                {provider.FirstName} {provider.LastName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {onboardingCardType === OnboardingDashboardCardType.UndocumentedOnboards && (
                                    <>
                                        <FormControl size="small" sx={{ minWidth: 150 }}>
                                            <InputLabel>Date Filter</InputLabel>
                                            <Select
                                                value={selectedFilter}
                                                label="Date Filter"
                                                onChange={(e) => setSelectedFilter(Number(e.target.value))}
                                            >
                                                {dayFilterList.map(date => (
                                                    <MenuItem key={date.Id} value={date.Id}>
                                                        {date.Value}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {selectedFilter === OnboardingWidgetDateRangeEnum.Custom && (
                                            <>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DesktopDatePicker
                                                        label="From Date"
                                                        value={fromDate || null}
                                                        minDate={new Date('2017-01-01')}
                                                        onChange={(date) => setFromDate(date)}
                                                        slotProps={{
                                                            textField: {
                                                                size: 'small',
                                                                sx: { width: 170 }
                                                            }
                                                        }}
                                                    />

                                                    <DesktopDatePicker
                                                        label="To Date"
                                                        value={toDate || null}
                                                        minDate={new Date('2017-01-01')}
                                                        onChange={(date) => setToDate(date)}
                                                        slotProps={{
                                                            textField: {
                                                                size: 'small',
                                                                sx: { width: 170 }
                                                            }
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </>
                                        )}
                                    </>
                                )}

                                {isActivityCard && (
                                    <>
                                        <FormControl size="small" sx={{ minWidth: 150 }}>
                                            <InputLabel>Date Filter</InputLabel>
                                            <Select
                                                value={activityFilter}
                                                label="Date Filter"
                                                onChange={(e) => setActivityFilter(Number(e.target.value) as OnboardingWidgetDateRangeEnum)}
                                            >
                                                {activityFilterList.map(date => (
                                                    <MenuItem key={date.Id} value={date.Id}>
                                                        {date.Value}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {activityFilter === OnboardingWidgetDateRangeEnum.Custom && (
                                            <>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DesktopDatePicker
                                                        label="From Date"
                                                        value={activityFromDate}
                                                        minDate={new Date('2017-01-01')}
                                                        onChange={(date) => setActivityFromDate(date)}
                                                        slotProps={{
                                                            textField: {
                                                                size: 'small',
                                                                sx: { width: 170 }
                                                            }
                                                        }}
                                                    />

                                                    <DesktopDatePicker
                                                        label="To Date"
                                                        value={activityToDate}
                                                        minDate={new Date('2017-01-01')}
                                                        onChange={(date) => setActivityToDate(date)}
                                                        slotProps={{
                                                            textField: {
                                                                size: 'small',
                                                                sx: { width: 170 }
                                                            }
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </>
                                        )}
                                    </>
                                )}

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title="Refresh">
                                        <IconButton
                                            onClick={loadDetails}
                                            disabled={loading}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                bgcolor: 'background.paper'
                                            }}
                                        >
                                            <RefreshOutlined />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        </CardContent>
                    </Card>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography fontWeight={600}>{title}</Typography>
                        </Box>

                        {loading ? (
                            <Box sx={{ minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CircularProgress />
                            </Box>
                        ) : details.length === 0 ? (
                            <Box sx={{ minHeight: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2 }}>
                                <Typography variant="h6" color="text.secondary">No records found</Typography>
                                <Typography variant="body2" color="text.secondary">Try changing the selected filters.</Typography>
                            </Box>
                        ) : (
                            <>
                                <TableContainer>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                {columns.map(column => (
                                                    <TableCell key={String(column.key)} align={column.align ?? 'left'} sx={{ fontWeight: 700, bgcolor: '#f8f9fb', whiteSpace: 'nowrap' }}>
                                                        {column.label}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {pagedDetails.map((row, rowIndex) => (
                                                <TableRow key={row.Id || row.MemberId || row.ProviderId || rowIndex} hover onClick={() => row.MemberId && navigateToMemberDashboard(row.MemberId)}>
                                                    {columns.map(column => (
                                                        <TableCell key={String(column.key)} align={column.align ?? 'left'} sx={{ whiteSpace: 'nowrap' }}>
                                                            {column.format ? column.format(row) : row[column.key] !== undefined && row[column.key] !== null && row[column.key] !== '' ? String(row[column.key]) : '-'}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    count={details.length}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    labelRowsPerPage="Rows per page:"
                                    onPageChange={(_, newPage) => setPage(newPage)}
                                    onRowsPerPageChange={event => {
                                        setRowsPerPage(parseInt(event.target.value, 10));
                                        setPage(0);
                                    }}
                                />
                            </>
                        )}
                    </Paper>
                </Box>
            </Grid>
        </Paper>
    );
};

export default OnboardingDashboardDetail;